import { useFileRejectionToast } from '@/hooks/fileUpload/useFileRejectionToast';
import { MAX_PATIENT_DOCUMENT_SIZE_BYTES, PATIENT_DOCUMENTS_ACCEPT } from '@heidi/utils';
import { useIntl } from 'react-intl';

const ALLOWED_EXTENSIONS = new Set<string>(
  Object.values(PATIENT_DOCUMENTS_ACCEPT)
    .flat()
    .map((ext) => ext.replace(/^\./, '').toLowerCase())
);

// The historic guard accepted by MIME OR extension so files dragged from
// apps that drop the extension (but still report `application/pdf`) were
// still allowed through. Preserve that parity by passing the MIME set to
// the shared hook as a fallback check.
const ALLOWED_MIME_TYPES = new Set<string>(Object.keys(PATIENT_DOCUMENTS_ACCEPT));

/**
 * Client-side guard for patient-document uploads.
 *
 * Returns a function that splits a freshly selected `File[]` into the files
 * that are an allowed type (PDF/Word) AND within the 25 MB cap, rejecting
 * the rest with a clear toast. Rejecting on selection (rather than letting
 * the upload round-trip and erroring) is what DEV-12173 asks for: the limit
 * is named and the upload is "not allowed upon attempt".
 *
 * Delegates to the shared `useFileRejectionToast` so the wording stays in
 * lockstep with chat attachments and any other surface that adopts the
 * same partition+toast contract.
 */
export function usePatientDocumentSizeGuard() {
  const intl = useIntl();

  const { partitionAndReport } = useFileRejectionToast({
    allowedExtensions: ALLOWED_EXTENSIONS,
    allowedMimeTypes: ALLOWED_MIME_TYPES,
    maxBytes: MAX_PATIENT_DOCUMENT_SIZE_BYTES,
    allowedFormatsLabel: intl.formatMessage({
      defaultMessage: 'PDF or Word documents',
      id: 'V38viyyaAz',
    }),
  });

  return partitionAndReport;
}
